import {
  ChatComposeBoxAddAttachmentMenuItem,
  ChatComposeBoxAddAttachmentMenuTrigger,
  ChatComposeBoxContent,
  ChatComposeBoxFooter,
  ChatComposeBoxRoot,
  ChatComposeBoxSendButton,
  ChatComposeBoxTextarea,
} from '@glade-ai/chat-plugin/ui';
import { DropdownMenu, DropdownMenuContent, Popover, PopoverAnchor, PopoverContent } from '@glade-ai/ui';
import { nanoid } from 'nanoid';
import { type KeyboardEvent, type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Descendant } from 'slate';

import Buttons from '@/components/Buttons';
import FieldLabel from '@/components/FormFields/FieldLabel';
import FileSelector from '@/components/FormFields/FileSelector';
import MessageTip from '@/components/MessageTip';
import SlateEditor, { slateHasText } from '@/components/SlateEditor';
import { CREDIT_REPORT_PRODUCT_SLUG } from '@/configuration/client';
import { KEY_CODES } from '@/helpers/appConsts';
import createRichTextAst from '@/helpers/createRichTextAst';
import getTextFromRichText from '@/helpers/getTextFromRichText';
import { PaymentGateModal, type PaymentGateModalProps } from '@/modals/PaymentGateModal';
import { logError } from '@/providers/ErrorTracking';
import { JobStatus, useJobContext } from '@/providers/Jobs';
import BeforeUnload from '@/providers/Jobs/JobsContainer/BeforeUnload';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { useScreenRecord } from '@/providers/ScreenRecord';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';
import * as ApiModels from '@/typings/api-models';
import { type Product } from '@/typings/graphql-models';

// Popover attachment menu items.
import {
  AddBookingMenuItem,
  AddBookingPopoverContent,
  AddCustomTermsMenuItem,
  AddCustomTermsPopoverContent,
  AddDocumentRequestMenuItem,
  AddDocumentRequestPopoverContent,
  AddFormRequestMenuItem,
  AddFormRequestPopoverContent,
  AddHandbookMenuItem,
  AddHandbookPopoverContent,
  AddInvoiceMenuItem,
  AddInvoicePopoverContent,
  AddLoomVideoMenuItem,
  AddPaymentGateMenuItem,
  AddPaymentLinkMenuItem,
  AddPaymentLinkPopoverContent,
  AddScreenRecordMenuItem,
  AddSoundMenuItem,
  AddSoundPopoverContent,
  AddVideoMenuItem,
  AttachUploadedRecordingMenuItem,
} from './attachment-menu-items';

// Attachments for the footer

import s from './InputComposer.module.scss';
import { Attachments, FileAttachment, HandbookAttachments, LoomAttachments } from './special-attachments';
import { ScreenRecordAttachments } from './special-attachments/ScreenRecordAttachments';

/** File with client-side ID for stable matching during upload. */
type FileWithClientId = {
  clientId: string;
  file: File | string;
};

type Media = {
  id: string;
  clientId?: string;
  name?: string | null;
  imageUrl?: string;
  mimetype: string;
  title: string;
  type?: string | null;
  url: string;
  uploadPercentage?: number;
};

type CommonProps = {
  onSend?: ({
    attachments,
    text,
    handbooks,
    medias,
  }: {
    attachments?: ApiModels.CreateMessageAttachment[];
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
  }) => Promise<void>;
  placeholder: string;
  areAttachmentsEnabled?: boolean;
  inputOptions?: Pick<ApiModels.Conversation, 'conversationStatus' | 'sid' | 'endedAt' | 'id'> | null;
  handleReopenThread?: () => void;
  isFetching: boolean;
  showAddAudio?: boolean;
  showAddVideo?: boolean;
  customAccept?: string;
  showChatEnded?: boolean;
  isLoomEnabled?: boolean;
  showProTips?: boolean;
  label?: string;
  showSendButton?: boolean;
  onChangeText?: (text: { children: Descendant[] }[]) => void;
  onChangeMedia?: (media: Pick<Media, 'id' | 'name'>[]) => void;
  onChangeFiles?: (files: (File | string)[]) => void;
  onChangeHandbooks?: (handbooks: Pick<Product, 'id' | 'title'>[]) => void;
  initialText?: { children: Descendant[] }[] | null;
  initialAttachments?: {
    referenceId: string;
    referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
    title: string;
    id: string;
  }[];
  initialMedia?: { id: string; name?: string | null }[];
  isRichText?: boolean;
  isDisabled?: boolean;
  initialHandbooks?: { id: string; title?: string | null }[];
  showAddHandbook?: boolean;
  showAddPaymentLink?: boolean;
  showAddAnyFile?: boolean;
  showAddCustomTerms?: boolean;
  showBankAccountIntegration?: boolean;
  referenceId: string;
  referenceType: string;
  onFocus?: () => void;

  currentMessage?: any;
  showAddDocumentRequest?: boolean;
  showAddCreditReport?: boolean;
  showAddFormRequest?: boolean;
  showAddBooking?: boolean;
  showAddInvoice?: boolean;
  /** Enables the "Payment Gate" item in the attachment menu. */
  showAddPaymentGate?: boolean;
  /** Workflow invoices available to the payment-gate modal's invoice selector. */
  paymentGateWorkflowInvoices?: PaymentGateModalProps['workflowInvoices'];
  /** Workflow contexts for the payment-gate modal (unused in current scope). */
  paymentGateWorkflowContexts?: PaymentGateModalProps['workflowContexts'];
  /** Persist a configured payment gate. Returns the id for the new attachment chip. */
  onSavePaymentGate?: (gate: ApiModels.PaymentGateConfig) => Promise<string | void> | string | void;
  isCreatorView?: boolean;
  isEmbeddedFromModal?: boolean;
  onChangeAttachments?: (
    attachments: {
      referenceId: string;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
      title: string;
      id: string;
    }[],
  ) => void;
};

export type ConversationComposeBoxProps =
  | (CommonProps & { isAsync: true; jobCorrelationId: string })
  | (CommonProps & { isAsync?: false; jobCorrelationId?: never });

type ActiveMenuItem =
  | null
  | 'sound'
  | 'handbook'
  | 'documentRequest'
  | 'formRequest'
  | 'paymentLink'
  | 'invoice'
  | 'customTerms'
  | 'booking';

/** Message composer with rich text, file uploads, and attachment menu. */
export const ConversationComposeBox = (props: ConversationComposeBoxProps): ReactElement => {
  const {
    areAttachmentsEnabled = true,
    onSend,
    placeholder,
    inputOptions,
    handleReopenThread,
    isFetching,
    showAddAudio = true,
    showAddVideo,
    customAccept = '*',
    isLoomEnabled = false,
    showProTips = true,
    label,
    showSendButton = true,
    onChangeText,
    onChangeMedia,
    onChangeFiles,
    onChangeHandbooks,
    onChangeAttachments,
    initialText,
    initialAttachments = [],
    initialHandbooks = [],
    initialMedia = [],
    isRichText = false,
    isDisabled = false,
    jobCorrelationId,
    isAsync,
    showAddCustomTerms = false,
    showBankAccountIntegration = false,
    showAddHandbook = false,
    showAddPaymentLink = false,
    showAddAnyFile = false,
    showAddDocumentRequest = false,
    showAddCreditReport = false,
    showAddFormRequest = false,
    showAddInvoice = false,
    showAddBooking = false,
    showAddPaymentGate = false,
    paymentGateWorkflowInvoices = [],
    paymentGateWorkflowContexts = [],
    onSavePaymentGate,
    referenceType,
    referenceId,
    onFocus,
    currentMessage,
  } = props;
  const [sendError, setSendError] = useState<Error | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [attachments, setAttachments] = useState<
    {
      data?: unknown;
      referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
      referenceId: string;
      title: string;
      id: string;
    }[]
  >(initialAttachments);
  const [filesToUpload, setFiles] = useState<FileWithClientId[]>(
    initialMedia?.map((media) => ({
      clientId: media.id,
      file: new File([], media.name || media.id),
    })) || [],
  );
  const [medias, setMedias] = useState<Pick<Media, 'id' | 'clientId' | 'name' | 'uploadPercentage' | 'type'>[]>(
    initialMedia?.map((media) => ({ ...media, clientId: nanoid() })) || [],
  );
  const [handbooks, setHandbooks] =
    useState<Pick<Product, 'id' | 'title' | 'image' | 'description'>[]>(initialHandbooks);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputFileRef = useRef<HTMLLabelElement>(null);
  const [isShimmeringReopenThread, setIsShimmeringReopenThread] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState<ActiveMenuItem>(null);
  const [isPaymentGateModalOpen, setIsPaymentGateModalOpen] = useState(false);
  const { jobs } = useJobContext();
  const { uploadedRecordings } = useScreenRecord();
  const suppressNextClose = useRef(false);

  const handleOpenPopover = useCallback((item: ActiveMenuItem) => {
    suppressNextClose.current = true;
    setActiveMenuItem(item);
  }, []);

  const handleClosePopover = useCallback(() => {
    if (suppressNextClose.current) {
      suppressNextClose.current = false;
    } else {
      setActiveMenuItem(null);
    }
  }, []);

  const handleFileUpload = async (files: string | File[]): Promise<void> => {
    const filesWithIds: FileWithClientId[] = [];
    if (typeof files === 'string') {
      filesWithIds.push({ clientId: nanoid(), file: files });
    } else {
      files.forEach((file) => {
        filesWithIds.push({ clientId: nanoid(), file });
      });
    }
    setFiles((prev) => [...prev, ...filesWithIds]);
  };
  const [value, setValue] = useState<{ children: Descendant[] }[]>(initialText || [createRichTextAst('')]);

  useEffect(() => {
    setSendError(null);
    if (onChangeText) {
      onChangeText(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setSendError(null);
    if (onChangeMedia) {
      onChangeMedia(medias);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medias]);

  useEffect(() => {
    setSendError(null);
    if (onChangeFiles) {
      onChangeFiles(filesToUpload.map((item) => item.file));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload]);

  useEffect(() => {
    setSendError(null);
    if (onChangeAttachments) {
      onChangeAttachments(attachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  useEffect(() => {
    if (onChangeHandbooks) {
      onChangeHandbooks(handbooks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handbooks]);

  const attachUploadedRecording = useCallback((mediaId: string, name: string) => {
    setMedias((previous) =>
      previous.some((media) => media.id === mediaId)
        ? previous
        : [...previous, { id: mediaId, name, type: 'screenRecording' }],
    );
  }, []);

  const screenRecordMedias = medias.filter((media) => media.type === 'screenRecording');

  const onSlateChange = (newValue: { children: Descendant[] }[]): void => {
    setValue(newValue);
  };

  const handleSend = async (newValue: { children: Descendant[] }[]): Promise<void> => {
    mixpanelTrack('InputComposer - send message');
    setSendError(null);
    setIsSending(true);
    try {
      await onSend?.({
        // @todo tsc says this is missing assignTo and that data isn't typed correctly.
        // This does work, fix tsc later.
        attachments: attachments as unknown as Parameters<typeof onSend>[0]['attachments'],
        handbooks: handbooks.map((h) => ({ id: h.id })),
        medias: medias.map((media) => ({ id: media.id })),
        text: newValue,
      });
      setValue([createRichTextAst('')]);
      setMedias([]);
      setFiles([]);
      setHandbooks([]);
      setAttachments([]);
    } catch (err) {
      logError(err, { tag: { flow: 'input-composer' } });
      setSendError(err as Error);
    }
    setIsSending(false);
  };

  const onUploadFile = ({ mediaId, name, clientId }: { mediaId: string; name: string; clientId?: string }): void => {
    setMedias((prev) => [...prev, { clientId, id: mediaId, name }]);
  };

  const hasText = useMemo(() => slateHasText(value), [value]);

  const conversationMessageTipKey = `conversations_sid_${inputOptions?.sid}`;
  const fileLoadingInProcess =
    medias.filter((media) => media.type !== 'loomVideo' && media.type !== 'screenRecording')?.length !==
    filesToUpload?.length;

  const loomUploadJobs = jobs.filter(
    (j) => j.correlationId === jobCorrelationId && j.status === JobStatus.IN_PROGRESS && j.metadata?.isLoom,
  );

  const loomMedias = medias.filter((media) => media.type === 'loomVideo');

  // disabled if there are no handbooks, medias, or text
  const hasContent =
    handbooks?.length > 0 ||
    (isAsync ? filesToUpload?.length > 0 || loomUploadJobs?.length > 0 : medias?.length > 0) ||
    hasText ||
    loomMedias?.length > 0 ||
    attachments?.length > 0;
  const disabled = isDisabled || false;
  const isSendButtonEnabled =
    !disabled &&
    hasContent &&
    (isAsync
      ? !jobs.find((j) => j.correlationId === jobCorrelationId && j.status === JobStatus.FAILED)
      : !fileLoadingInProcess);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      }

      e.preventDefault();

      const textValue = getTextFromRichText(value);

      if (!textValue.trim()) return;

      handleSend(value);
    } else if (e.keyCode === KEY_CODES.ENTER && (e.ctrlKey || e.altKey || e.shiftKey) && isSendButtonEnabled) {
      e.preventDefault();
      e.stopPropagation();
      handleSend(value);
    } else if (e.keyCode === KEY_CODES.ENTER && !(e.ctrlKey || e.altKey || e.shiftKey)) {
      // Fixed enter for new line for iphone
      e.preventDefault();
      // add \n to the current position of the cursor
      const cursorPosition = e.currentTarget.selectionStart;
      const textBeforeCursor = e.currentTarget.value.substring(0, cursorPosition);
      const textAfterCursor = e.currentTarget.value.substring(cursorPosition, e.currentTarget.value?.length);
      setValue([createRichTextAst(`${textBeforeCursor}\n${textAfterCursor}`)]);
      // make sure the cursor remains in the same position
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.selectionStart = cursorPosition + 1;
          inputRef.current.selectionEnd = cursorPosition + 1;
        }
      }, 0);
    }
  };

  const handleAttachHandbook = (handbook: Pick<Product, 'id' | 'title' | 'image' | 'description'>): void => {
    setHandbooks((oldHandbooks) => [
      ...oldHandbooks,
      { id: handbook.id, image: handbook.image, title: handbook.title },
    ]);
  };

  const addDocumentRequestTemplateAttachment = ({
    title,
    documentRequestTemplateId,
  }: {
    title: string;
    documentRequestTemplateId: string;
  }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: documentRequestTemplateId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_TEMPLATE,
        title,
      },
    ]);
  };

  const addCreditReportAttachment = (): void => {
    const id = CREDIT_REPORT_PRODUCT_SLUG;
    const title = 'Purchase credit report';

    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: id,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT,
        title,
      },
    ]);
  };

  const addBankAccountIntegrationAttachment = (): void => {
    const title = 'Bank Account Integration';
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: 'bank-account-integration',
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION,
        title,
      },
    ]);
  };

  const addFormRequestAttachment = ({ title, formTemplateId }: { title: string; formTemplateId: string }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: formTemplateId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST_TEMPLATE,
        title,
      },
    ]);
  };

  const handleAddInvoice = ({ invoiceTemplateId, title }: { invoiceTemplateId: string; title: string }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: invoiceTemplateId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE_REQUEST,
        title,
      },
    ]);
  };

  const handleAddBooking = ({ priceId, title }: { priceId: string; title: string }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: priceId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING_PRICE,
        title,
      },
    ]);
  };

  const handleAddCustomTerms = ({
    customTermsTemplateId,
    title,
  }: {
    customTermsTemplateId: string;
    title: string;
  }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: customTermsTemplateId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS_TEMPLATE,
        title,
      },
    ]);
  };

  const handleAddPaymentLink = ({ priceId, title }: { priceId: string; title: string }): void => {
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: priceId,
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_LINK,
        title,
      },
    ]);
  };

  const handleSavePaymentGate = async (values: ApiModels.PaymentGateConfig[]): Promise<void> => {
    const gate = values[0];
    if (!gate || !onSavePaymentGate) return;
    const savedId = await onSavePaymentGate(gate);
    const invoiceLabel =
      paymentGateWorkflowInvoices.find((invoice) => invoice.id === gate.invoiceTemplateRef)?.label ?? 'Invoice';
    const threshold =
      gate.gateType === ApiModels.PAYMENT_GATE_TYPES.MINIMUM
        ? `$${Math.round((gate.minimumAmount ?? 0) / 100)} min`
        : `${gate.minimumPercentage ?? 0}% min`;
    setAttachments((prev) => [
      ...prev,
      {
        id: nanoid(),
        referenceId: typeof savedId === 'string' ? savedId : nanoid(),
        referenceType: MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE,
        title: `Payment Gate — ${invoiceLabel} — ${threshold}`,
      },
    ]);
    setIsPaymentGateModalOpen(false);
  };

  const onToolbarClick = (type: string, accept: string): void => {
    if (type === 'file' && inputFileRef.current) {
      (inputFileRef.current.firstChild as HTMLInputElement)?.setAttribute('accept', accept);
      inputFileRef.current.click();
    }
  };

  const handleAttachLink = (link: string): void => {
    handleSend([createRichTextAst(link)]);
  };

  const handleAddFiles = (el: Media): void => {
    setMedias((prevMedias) => [...prevMedias, el]);
  };

  const removeHandbook = (media: Pick<Product, 'id' | 'title'>): void => {
    setHandbooks(handbooks.filter((h) => h.id !== media.id));
  };

  const removeAttachment = (attachmentId: string): void => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const attachLoomMedia = (media: Pick<Media, 'id' | 'name' | 'type'>): void => {
    setMedias((prevMedias) => [...prevMedias, media]);
  };

  const handleRemoveFile = (clientId: string) => () => {
    setFiles((prevFiles) => prevFiles.filter((item) => item.clientId !== clientId));
  };

  const fetchFiles = async (mediaFiles: Media[]): Promise<void> => {
    const filesWithIds = await Promise.all(
      (mediaFiles || []).map(async (media: Media) => {
        const response = await fetch(media.url);
        const blob = await response.blob();
        return {
          clientId: nanoid(),
          file: new File([blob], media.name || media.title, { type: blob.type }),
        };
      }),
    );
    setFiles(filesWithIds || []);
  };

  useEffect(() => {
    if (currentMessage) {
      fetchFiles(currentMessage.medias);
      setValue((currentMessage.text && [createRichTextAst(currentMessage.text)]) || [createRichTextAst('')]);
      setMedias(currentMessage.medias.map((media: { id: string }) => ({ id: media.id })));
      setHandbooks(currentMessage.products || []);
      setAttachments(currentMessage.attachments || []);
    }
  }, [currentMessage]);

  return (
    <>
      {filesToUpload?.length > 0 && <BeforeUnload />}
      <FileSelector id={jobCorrelationId} ref={inputFileRef} uploadFunction={handleFileUpload} />
      {label && <FieldLabel id='input-composer-label'>{label}</FieldLabel>}
      <Popover open={activeMenuItem !== null} onOpenChange={(open) => !open && handleClosePopover()}>
        {inputOptions?.conversationStatus === ApiModels.ConversationStatus.Completed ? (
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '16px 0',
              width: '100%',
            }}
          >
            <p className='body-md' style={{ color: 'var(--color-gray-75)' }}>
              Thread Closed
            </p>
            {handleReopenThread && (
              <Buttons
                isSecondary
                onClick={() => {
                  handleReopenThread();
                  setIsShimmeringReopenThread(true);
                }}
                isShimmering={isShimmeringReopenThread}
              >
                Reopen Thread
              </Buttons>
            )}
          </div>
        ) : (
          <>
            <ChatComposeBoxRoot>
              <ChatComposeBoxContent>
                {areAttachmentsEnabled && (
                  <DropdownMenu>
                    <PopoverAnchor asChild>
                      <ChatComposeBoxAddAttachmentMenuTrigger />
                    </PopoverAnchor>
                    <DropdownMenuContent
                      side='top'
                      onCloseAutoFocus={(event) => {
                        /* TODO(will): Manage focus passing better. */ event.preventDefault();
                      }}
                    >
                      <ChatComposeBoxAddAttachmentMenuItem
                        icon='ph--image--regular'
                        label='Image'
                        onClick={() => onToolbarClick('file', 'image/*')}
                        disabled={disabled}
                      />
                      {showAddAudio && (
                        <AddSoundMenuItem onClick={() => handleOpenPopover('sound')} disabled={disabled} />
                      )}
                      {showAddVideo && (
                        <AddVideoMenuItem onClick={() => onToolbarClick('file', 'video/*')} disabled={disabled} />
                      )}
                      {isLoomEnabled && (
                        <AddLoomVideoMenuItem onAttachLoomMedia={attachLoomMedia} jobCorrelationId={jobCorrelationId} />
                      )}
                      <AddScreenRecordMenuItem disabled={disabled} />
                      {uploadedRecordings.map((recording) => (
                        <AttachUploadedRecordingMenuItem
                          key={recording.mediaId}
                          recording={recording}
                          onAttachRecording={attachUploadedRecording}
                          disabled={disabled}
                        />
                      ))}
                      {showAddAnyFile && (
                        <ChatComposeBoxAddAttachmentMenuItem
                          icon='ph--file--regular'
                          label='File'
                          onClick={() => onToolbarClick('file', customAccept)}
                          disabled={disabled}
                        />
                      )}
                      {showAddHandbook && (
                        <AddHandbookMenuItem onClick={() => handleOpenPopover('handbook')} disabled={disabled} />
                      )}
                      {showAddDocumentRequest && (
                        <AddDocumentRequestMenuItem
                          onClick={() => handleOpenPopover('documentRequest')}
                          disabled={disabled}
                        />
                      )}
                      {showAddFormRequest && (
                        <AddFormRequestMenuItem onClick={() => handleOpenPopover('formRequest')} disabled={disabled} />
                      )}
                      {showAddCreditReport && (
                        <ChatComposeBoxAddAttachmentMenuItem
                          icon='ph--identification-card--regular'
                          label='Purchase Credit Report'
                          onClick={addCreditReportAttachment}
                          disabled={disabled}
                        />
                      )}
                      {showBankAccountIntegration && (
                        <ChatComposeBoxAddAttachmentMenuItem
                          icon='ph--bank--regular'
                          label='Bank Account Integration'
                          onClick={addBankAccountIntegrationAttachment}
                          disabled={disabled}
                        />
                      )}
                      {showAddPaymentLink && (
                        <AddPaymentLinkMenuItem onClick={() => handleOpenPopover('paymentLink')} disabled={disabled} />
                      )}
                      {showAddInvoice && (
                        <AddInvoiceMenuItem onClick={() => handleOpenPopover('invoice')} disabled={disabled} />
                      )}
                      {showAddPaymentGate && (
                        <AddPaymentGateMenuItem onClick={() => setIsPaymentGateModalOpen(true)} disabled={disabled} />
                      )}
                      {showAddCustomTerms && (
                        <AddCustomTermsMenuItem onClick={() => handleOpenPopover('customTerms')} disabled={disabled} />
                      )}
                      {showAddBooking && (
                        <AddBookingMenuItem onClick={() => handleOpenPopover('booking')} disabled={disabled} />
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {isRichText ? (
                  <SlateEditor
                    id='input'
                    name='input'
                    placeholder={placeholder}
                    slateValue={value}
                    onChange={onSlateChange}
                    isMediaButton={false}
                    containerClassName={s.slateEditor}
                    {...(onFocus ? { onFocus } : {})}
                  />
                ) : (
                  <ChatComposeBoxTextarea
                    ref={inputRef}
                    value={getTextFromRichText(value)}
                    onChange={(nextValue) => {
                      setValue([createRichTextAst(nextValue || '')]);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled || isSending}
                    {...(onFocus ? { onFocus } : {})}
                  />
                )}

                {showSendButton && (
                  <ChatComposeBoxSendButton
                    disabled={!isSendButtonEnabled || isSending || isFetching}
                    onClick={() => handleSend(value)}
                  />
                )}
              </ChatComposeBoxContent>

              {sendError && (
                <ChatComposeBoxFooter>
                  <span className='caption' style={{ color: 'var(--color-error)' }}>
                    Failed to send message. Please try again.
                  </span>
                </ChatComposeBoxFooter>
              )}

              {(filesToUpload?.length > 0 ||
                !!handbooks?.length ||
                loomMedias?.length > 0 ||
                loomUploadJobs?.length > 0 ||
                screenRecordMedias?.length > 0 ||
                attachments?.length > 0) && (
                <ChatComposeBoxFooter>
                  {filesToUpload?.length > 0 && (
                    <>
                      {filesToUpload.map(({ clientId, file }) => (
                        <FileAttachment
                          isAsync={isAsync}
                          key={clientId}
                          clientId={clientId}
                          file={file}
                          media={medias.find((media) => media.id === clientId)}
                          onUploadFile={onUploadFile}
                          jobCorrelationId={jobCorrelationId}
                          referenceId={referenceId}
                          referenceType={referenceType}
                          removeFile={handleRemoveFile(clientId)}
                          removeMedia={(mediaId: string) => setMedias(medias.filter((f) => f.id !== mediaId))}
                        />
                      ))}
                    </>
                  )}
                  {(loomMedias?.length > 0 || loomUploadJobs?.length > 0) && (
                    <LoomAttachments medias={loomMedias} uploadJobs={loomUploadJobs} />
                  )}
                  {screenRecordMedias?.length > 0 && <ScreenRecordAttachments medias={screenRecordMedias} />}
                  {!!handbooks?.length && <HandbookAttachments handbooks={handbooks} removeHandbook={removeHandbook} />}
                  <Attachments attachments={attachments} removeAttachment={removeAttachment} />
                </ChatComposeBoxFooter>
              )}
            </ChatComposeBoxRoot>
          </>
        )}

        <PopoverContent
          side='top'
          align='start'
          className='max-h-[calc(var(--radix-popover-content-available-height)-1rem)] overflow-y-auto'
        >
          {activeMenuItem === 'sound' && (
            <AddSoundPopoverContent
              onSubmit={(url) => {
                handleFileUpload(url);
                handleClosePopover();
              }}
              onFileUploadClicked={() => {
                onToolbarClick('file', 'audio/*');
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'handbook' && (
            <AddHandbookPopoverContent
              onSendMessage={(product) => {
                handleAttachHandbook(product);
                handleClosePopover();
              }}
              onAddLink={(link) => {
                handleAttachLink(link);
                handleClosePopover();
              }}
              onAddFiles={(files) => {
                handleAddFiles(files);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'documentRequest' && (
            <AddDocumentRequestPopoverContent
              onAttach={(data) => {
                addDocumentRequestTemplateAttachment(data);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'formRequest' && (
            <AddFormRequestPopoverContent
              onAttach={(data) => {
                addFormRequestAttachment(data);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'paymentLink' && (
            <AddPaymentLinkPopoverContent
              onAttach={(data) => {
                handleAddPaymentLink(data);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'invoice' && (
            <AddInvoicePopoverContent
              onAttach={(data) => {
                handleAddInvoice(data);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'customTerms' && (
            <AddCustomTermsPopoverContent
              onAttach={(data) => {
                handleAddCustomTerms(data);
                handleClosePopover();
              }}
            />
          )}

          {activeMenuItem === 'booking' && (
            <AddBookingPopoverContent
              onAttach={(data) => {
                handleAddBooking(data);
                handleClosePopover();
              }}
            />
          )}
        </PopoverContent>
      </Popover>

      <PaymentGateModal
        isOpen={isPaymentGateModalOpen}
        onClose={() => setIsPaymentGateModalOpen(false)}
        workflowInvoices={paymentGateWorkflowInvoices}
        workflowContexts={paymentGateWorkflowContexts}
        onSave={handleSavePaymentGate}
      />

      {/* TODO - move this outside of InputComposer component */}
      {showProTips && (
        <MessageTip containerClassName={s['conv-message-tip']} messageTipKey={conversationMessageTipKey}>
          <b className='body-md-bold'>👋Pro tips to make the most of your chat:</b>
          <ol className={s['conv-message-tip__list']}>
            <li className={s['conv-message-tip__list-item']}>
              1. Keep your questions under
              <b>&nbsp;2 minutes for video&nbsp;</b>
              or
              <b>&nbsp;2 sentences for text.&nbsp;</b>
            </li>
            <li className={s['conv-message-tip__list-item']}>
              2. Focus on
              <b>&nbsp;one question&nbsp;</b>
              at a time.
            </li>
          </ol>
        </MessageTip>
      )}
    </>
  );
};
